import { useUserStore } from '@/stores/user/userStore';
import useTeamManagementStore from '@/stores/teamManagement/teamManagementStore';
import useLayoutStore from '@/stores/layout/layoutStore';
import { defaultConversationStore } from '@/stores/conversation/conversationStore';
import { useCustomizationStore } from '@/stores/customization/customizationStore';
import { useChatbotStore } from '@/stores/chatbot/chatbotStore';
import useNotificationsStore from '@/stores/notifications/notifications';
import useToastStore from '@/stores/toast/toastStore';
import { useHumanHandoverStore } from '@/stores/humanHandover/humanHandoverStore';
import useNewUpdatesModalStore from '@/stores/newUpdatesModal/newUpdatesModalStore';

export const isLoggedInDemoUser = () => {
  const { user, auth } = useUserStore.getState();
  return (
    Boolean(user?.is_demo_account || user?.is_demo) && Boolean(auth?.access_token)
  );
};

/**
 * Clears persisted auth, user, and related client stores — same as logout, without navigation.
 */
export const clearClientSession = () => {
  useUserStore.getState().removeAuthDetail();
  useUserStore.getState().reset();
  useTeamManagementStore.getState().reset();
  useLayoutStore.getState().reset();
  defaultConversationStore.getState().reset();
  useCustomizationStore.getState().reset();
  useChatbotStore.getState().reset();
  useNotificationsStore.getState().reset();
  useToastStore.getState().reset();
  useHumanHandoverStore.getState().reset();
  useNewUpdatesModalStore.getState().reset();

  localStorage.clear();
  sessionStorage.clear();
  document.documentElement.classList.remove('dark');
};

const DEMO_MAGIC_LINK_PARAM = 'from_demo_magic_link';

/** Carried on the URL through demo logout → magic link login so Mixpanel can alias demo → real user. */
export const DEMO_SOURCE_USER_ID_PARAM = 'demo_source_user_id';

/**
 * Full reload: clear demo session so the magic link can issue a real account JWT,
 * then return to login with the same token and a flag for post-login redirect.
 * @param {string} token
 * @param {string} [demoSourceUserId] — demo `user.id` for Mixpanel alias after signup (before clearClientSession)
 */
export const redirectToLoginWithMagicLinkAfterDemoLogout = (token, demoSourceUserId) => {
  clearClientSession();
  const params = new URLSearchParams({
    token,
    [DEMO_MAGIC_LINK_PARAM]: '1',
  });
  if (demoSourceUserId) {
    params.set(DEMO_SOURCE_USER_ID_PARAM, demoSourceUserId);
  }
  window.location.href = `/log-in?${params.toString()}`;
};

export const isDemoMagicLinkRedirect = (searchParams) =>
  searchParams.get(DEMO_MAGIC_LINK_PARAM) === '1';

/** Preserve demo Mixpanel id when navigating after magic-link login (e.g. /chatbot/create). */
export const appendDemoSourceUserIdToPath = (pathname, searchParams) => {
  const id = searchParams.get(DEMO_SOURCE_USER_ID_PARAM);
  if (!id) return pathname;
  const q = new URLSearchParams({ [DEMO_SOURCE_USER_ID_PARAM]: id });
  return `${pathname}?${q.toString()}`;
};
